import { AlertTriangle, CheckCircle, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TurnoverStatus } from "@/lib/tankCalculations";

export interface TurnoverRateDisplayProps {
  /** 循环倍率（次/小时），null 表示无法计算 */
  rate: number | null;
  /** 循环倍率状态 */
  status: TurnoverStatus | null;
  /** 流量单位标签 */
  flowRateUnit?: string;
  /** 自定义类名 */
  className?: string;
  /** 未设置流量时的提示文本 */
  noFlowRateText?: string;
  /** 循环倍率标签 */
  label?: string;
  /** 单位后缀（次/小时） */
  unitSuffix?: string;
  /** 低循环提示 */
  lowTip?: string;
  /** 高循环提示 */
  highTip?: string;
}

/**
 * 循环倍率展示组件
 *
 * 展示循环倍率数值，根据状态显示不同颜色/图标：
 * - low (<3): 黄色警告，提示过滤不足
 * - normal (3-10): 绿色正常
 * - high (>10): 蓝色，提示强力过滤
 *
 * @example
 * ```tsx
 * <TurnoverRateDisplay
 *   rate={7.5}
 *   status="normal"
 *   flowRateUnit="L/h"
 * />
 * ```
 */
export function TurnoverRateDisplay({
  rate,
  status,
  className,
  noFlowRateText = "未设置水泵流量",
  label = "循环倍率",
  unitSuffix = "次/小时",
  lowTip = "过滤可能不足",
  highTip = "强力过滤",
}: TurnoverRateDisplayProps) {
  // 无法计算循环倍率时显示占位符
  if (rate === null || status === null) {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-2 text-muted-foreground",
          className
        )}
      >
        <span className="text-sm">{label}</span>
        <span className="text-sm italic">{noFlowRateText}</span>
      </div>
    );
  }

  // 根据状态获取样式配置
  const statusConfig = getStatusConfig(status);

  return (
    <div className={cn("flex items-center justify-between gap-2", className)}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className={cn("flex items-center gap-1", statusConfig.textColor)}>
          <statusConfig.Icon className="h-4 w-4" />
          <span className="font-medium">
            {rate.toFixed(1)} {unitSuffix}
          </span>
        </div>
        {status !== "normal" && (
          <span className={cn("text-xs", statusConfig.textColor)}>
            ({status === "low" ? lowTip : highTip})
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * 获取状态对应的样式配置
 */
function getStatusConfig(status: TurnoverStatus) {
  switch (status) {
    case "low":
      return {
        Icon: AlertTriangle,
        textColor: "text-yellow-600 dark:text-yellow-500",
        bgColor: "bg-yellow-50 dark:bg-yellow-950",
      };
    case "high":
      return {
        Icon: Zap,
        textColor: "text-blue-600 dark:text-blue-500",
        bgColor: "bg-blue-50 dark:bg-blue-950",
      };
    case "normal":
    default:
      return {
        Icon: CheckCircle,
        textColor: "text-green-600 dark:text-green-500",
        bgColor: "bg-green-50 dark:bg-green-950",
      };
  }
}

export default TurnoverRateDisplay;
